// initial state
const state = {
  stats: null,
  error: null,
  serverStatus: {'silver': [], 'gold': []},
  pokeTime: {'silver': 0, 'gold': 0},
  onlineHistory: null,
  generatedAt: null,
  statsUpdatedAt: null,
  maxStatsDays: null
}

// getters
const getters = {
  stats: state => state.stats,
  RestartError: state => state.error,
  statusSilver: state => state.serverStatus.silver,
  statusGold: state => state.serverStatus.gold,
  pokeTime: state => state.pokeTime,
  onlineHistory: state => state.onlineHistory,
  dashboardGeneratedAt: state => state.generatedAt,
  dashboardStatsUpdatedAt: state => state.statsUpdatedAt,
  dashboardMaxStatsDays: state => state.maxStatsDays
}

// actions
const actions = {
  getDashboard ({ commit }, pokemon) {
    commit('setDashboard', pokemon.data)
  },
  updateDashboardStats ({ commit }, payload) {
    commit('updateDashboardStats', payload.data)
  },
  restartError ({ commit }, error) {
    commit('setRestartError', error.data)
  },
  status ({ commit }, newStatus) {
    commit('addStatus', newStatus.data)
  }
}

// mutations
const mutations = {
  setDashboard (state, dashboard) {
    if (dashboard.stats) {
      if (!state.stats) state.stats = {}
      if (!sameSimpleList(state.stats.money_toplist, dashboard.stats.money_toplist)) {
        state.stats.money_toplist = dashboard.stats.money_toplist
      }
      if (!sameSimpleList(state.stats.losses_toplist, dashboard.stats.losses_toplist)) {
        state.stats.losses_toplist = dashboard.stats.losses_toplist
      }
      if (!sameSimpleList(state.stats.wins_toplist, dashboard.stats.wins_toplist)) {
        state.stats.wins_toplist = dashboard.stats.wins_toplist
      }
      if (!sameSeriesMap(state.stats.active_players, dashboard.stats.active_players)) {
        state.stats.active_players = dashboard.stats.active_players
      }
      if (!sameSeriesMap(state.stats.new_players, dashboard.stats.new_players)) {
        state.stats.new_players = dashboard.stats.new_players
      }
      if (!sameSeriesMap(state.stats.average_money, dashboard.stats.average_money)) {
        state.stats.average_money = dashboard.stats.average_money
      }
      state.statsUpdatedAt = dashboard.stats.updated_at || state.statsUpdatedAt
    }
    if (dashboard.status) {
      state.serverStatus = dashboard.status
    }
    if (dashboard.poke_time) {
      state.pokeTime = dashboard.poke_time
    }
    if (dashboard.online_history) {
      if (!sameHistory(state.onlineHistory, dashboard.online_history)) {
        state.onlineHistory = dashboard.online_history
      }
    }
    state.generatedAt = dashboard.generated_at || null
    state.maxStatsDays = dashboard.max_stats_days || state.maxStatsDays
    for (let key in dashboard.poke_time) {
      if (key in state.pokeTime) {
        state.pokeTime[key] = dashboard.poke_time[key] - Date.now() / 1000
      }
    }
  },
  addStatus (state, newStatus) {
    for (let key in newStatus) {
      if (key in state.serverStatus) {
        state.serverStatus[key].push(newStatus[key][0])
      }
      if (state.onlineHistory && key in state.onlineHistory) {
        state.onlineHistory[key].push({
          count: newStatus[key][0].player_count,
          peak: newStatus[key][0].player_count,
          average: newStatus[key][0].player_count,
          date: new Date().toISOString()
        })
        const cutoff = Date.now() - (7 * 24 * 60 * 60 * 1000)
        state.onlineHistory[key] = state.onlineHistory[key]
          .filter(point => new Date(point.date).getTime() >= cutoff)
      }
    }
  },
  updateDashboardStats (state, payload) {
    if (!payload) return
    if (!state.stats) state.stats = {}
    if ('money_toplist' in payload && !sameSimpleList(state.stats.money_toplist, payload.money_toplist)) {
      state.stats.money_toplist = payload.money_toplist
    }
    if ('losses_toplist' in payload && !sameSimpleList(state.stats.losses_toplist, payload.losses_toplist)) {
      state.stats.losses_toplist = payload.losses_toplist
    }
    if ('wins_toplist' in payload && !sameSimpleList(state.stats.wins_toplist, payload.wins_toplist)) {
      state.stats.wins_toplist = payload.wins_toplist
    }
    if ('active_players' in payload && !sameSeriesMap(state.stats.active_players, payload.active_players)) {
      state.stats.active_players = payload.active_players
    }
    if ('new_players' in payload && !sameSeriesMap(state.stats.new_players, payload.new_players)) {
      state.stats.new_players = payload.new_players
    }
    if ('average_money' in payload && !sameSeriesMap(state.stats.average_money, payload.average_money)) {
      state.stats.average_money = payload.average_money
    }
    if (payload.updated_at) state.statsUpdatedAt = payload.updated_at
  },
  setRestartError (state, newStatus) {
    state.error = {error: newStatus, date: Date.now()}
  }
}

function sameHistory (a, b) {
  if (!a || !b) return false
  const keys = Object.keys(b)
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]
    const av = normalizeSeries(a[key] || [])
    const bv = normalizeSeries(b[key] || [])
    if (av.length !== bv.length) return false
    if (bv.length === 0) continue
    const al = av[av.length - 1]
    const bl = bv[bv.length - 1]
    if (!al || !bl) return false
    if (String(al.date) !== String(bl.date) || al.count !== bl.count || al.peak !== bl.peak || al.average !== bl.average) return false
  }
  return true
}

function sameSeriesMap (a, b) {
  if (!a || !b) return false
  const keys = Object.keys(b)
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]
    const av = normalizeSeries(a[key] || [])
    const bv = normalizeSeries(b[key] || [])
    if (av.length !== bv.length) return false
    if (bv.length === 0) continue
    const al = av[av.length - 1]
    const bl = bv[bv.length - 1]
    if (!al || !bl) return false
    if (String(al.date) !== String(bl.date) || al.count !== bl.count || al.peak !== bl.peak || al.average !== bl.average) return false
  }
  return true
}

function sameSimpleList (a, b) {
  if (!a || !b) return false
  if (a.length !== b.length) return false
  if (a.length === 0) return true
  const al = a[a.length - 1]
  const bl = b[b.length - 1]
  if (!al || !bl) return false
  return JSON.stringify(al) === JSON.stringify(bl)
}

function normalizeSeries (series) {
  return (Array.isArray(series) ? series.slice() : []).sort((a, b) => {
    return new Date(a.date).getTime() - new Date(b.date).getTime()
  })
}

export default {
  state,
  getters,
  actions,
  mutations
}
